import { AppError, toAppError } from "../types/errors";
import { fetchAndProcessSSE } from "../utils/sseUtils";
import { logWarn } from "../utils/logging";

export type SSEConnectionState =
  | "idle"
  | "connecting"
  | "open"
  | "reconnecting"
  | "closed"
  | "error";

export interface BackoffConfig {
  baseMs: number; // e.g., 1000
  factor: number; // e.g., 2
  maxMs: number; // e.g., 5000
  maxAttempts?: number; // undefined => unlimited (until overall timeout/abort)
  jitter?: boolean; // default true
}

export interface ResilientSSEOptions<TRequestBody = Record<string, unknown>> {
  url: string;
  method?: "GET" | "POST";
  body?: TRequestBody;
  signal?: AbortSignal; // external abort (e.g., component unmount)
  onOpen?: () => void;
  onMessage?: (data: string, eventType?: string) => void; // raw SSE messages
  onEnd?: (summaryJson?: string) => void; // optional payload on end
  onError?: (error: AppError) => void; // terminal errors only
  onStateChange?: (state: SSEConnectionState, attempt: number) => void;
  backoff?: BackoffConfig;
  stallTimeoutMs?: number; // e.g., 15000
  overallTimeoutMs?: number; // stop trying after this time budget
}

export interface ResilientSSEHandle {
  close: () => void;
  getState: () => SSEConnectionState;
  attempts: number;
  completed: Promise<void>;
}

function computeDelayMs(attempt: number, cfg: BackoffConfig): number {
  const exp = Math.pow(cfg.factor, Math.max(0, attempt - 1));
  const raw = Math.min(cfg.maxMs, cfg.baseMs * exp);
  if (cfg.jitter === false) return raw;
  return Math.random() * raw; // full jitter
}

function isTerminalError(appErr: AppError): boolean {
  const status = appErr.status ?? 500;
  // Treat 4xx as terminal. 5xx/network are retriable.
  return status >= 400 && status < 500;
}

/**
 * Creates a resilient SSE session built on top of fetch+ReadableStream processing.
 * Handles reconnects with exponential backoff and detects stalled streams.
 */
export function createResilientSSE<TRequestBody = Record<string, unknown>>(
  options: ResilientSSEOptions<TRequestBody>,
): ResilientSSEHandle {
  const {
    url,
    method = "POST",
    body,
    signal: externalSignal,
    onOpen,
    onMessage,
    onEnd,
    onError,
    onStateChange,
    backoff = {
      baseMs: 1000,
      factor: 2,
      maxMs: 5000,
      maxAttempts: 6,
      jitter: true,
    },
    stallTimeoutMs = 60000, // Increased from 15s to 60s for slower LLM models
    overallTimeoutMs,
  } = options;

  let state: SSEConnectionState = "idle";
  let attempts = 0;

  let closedByUser = false;
  let currentAbort: AbortController | null = null;
  let stallTimer: ReturnType<typeof setTimeout> | null = null;
  let overallTimer: ReturnType<typeof setTimeout> | null = null;
  let lastActivityAt = Date.now();

  let completedResolve: () => void;
  let completedReject: (err: unknown) => void;
  const completed = new Promise<void>((resolve, reject) => {
    completedResolve = resolve;
    completedReject = reject;
  });

  const setState = (next: SSEConnectionState) => {
    state = next;
    try {
      onStateChange?.(state, attempts);
    } catch (e) {
      logWarn("[sseClient] onStateChange threw:", e);
    }
  };

  const clearStallTimer = () => {
    if (stallTimer) {
      clearTimeout(stallTimer);
      stallTimer = null;
    }
  };

  const armStallTimer = () => {
    clearStallTimer();
    if (!stallTimeoutMs || stallTimeoutMs <= 0) return;
    stallTimer = setTimeout(() => {
      const since = Date.now() - lastActivityAt;
      logWarn(
        `[sseClient] Stall detected after ${since}ms without events. Reconnecting...`,
      );
      // Abort current attempt to trigger reconnect
      if (currentAbort && !currentAbort.signal.aborted) {
        currentAbort.abort();
      }
    }, stallTimeoutMs);
  };

  const updateActivity = () => {
    lastActivityAt = Date.now();
    armStallTimer();
  };

  const cleanup = () => {
    clearStallTimer();
    if (currentAbort && !currentAbort.signal.aborted) {
      currentAbort.abort();
    }
    currentAbort = null;
  };

  const close = () => {
    closedByUser = true;
    cleanup();
    if (overallTimer) {
      clearTimeout(overallTimer);
      overallTimer = null;
    }
    setState("closed");
    completedResolve();
  };

  if (overallTimeoutMs && overallTimeoutMs > 0) {
    overallTimer = setTimeout(() => {
      logWarn(
        `[sseClient] Overall timeout of ${overallTimeoutMs}ms exceeded. Stopping.`,
      );
      setState("error");
      cleanup();
      completedReject(new Error("SSE overall timeout exceeded"));
    }, overallTimeoutMs);
  }

  const externalAborted = () => externalSignal?.aborted === true;

  const run = async () => {
    while (true) {
      if (closedByUser || externalAborted()) {
        setState("closed");
        return completedResolve();
      }

      attempts += 1;
      setState(attempts === 1 ? "connecting" : "reconnecting");
      currentAbort = new AbortController();

      // If external signal aborts, also abort the current attempt
      const onExternalAbort = () => {
        if (currentAbort && !currentAbort.signal.aborted) currentAbort.abort();
      };
      externalSignal?.addEventListener("abort", onExternalAbort, {
        once: true,
      });

      let ended = false;
      let terminalError: AppError | null = null;

      try {
        updateActivity();
        onOpen?.();

        await fetchAndProcessSSE({
          url,
          method,
          body,
          signal: currentAbort.signal,
          onOpen: () => {
            setState("open");
            updateActivity();
          },
          onMessage: (data: string, eventType?: string) => {
            updateActivity();
            if (eventType === "error") {
              // Defensive: map to AppError and mark as terminal
              try {
                const parsed = JSON.parse(data);
                terminalError = toAppError(parsed, 500);
              } catch {
                terminalError = toAppError(data, 500);
              }
            } else if (eventType === "end") {
              ended = true;
              onEnd?.(data);
            } else {
              onMessage?.(data, eventType);
            }
          },
          onEnd: () => {
            ended = true;
            setState("closed");
            onEnd?.();
          },
          onError: (err: AppError) => {
            // Stream read/connect error. Decide retriable vs terminal below.
            terminalError = err;
          },
        });
      } catch (e) {
        // fetchAndProcessSSE can throw for initial connection failures
        const appErr = toAppError(e, 500);
        terminalError = appErr;
      } finally {
        externalSignal?.removeEventListener("abort", onExternalAbort);
        clearStallTimer();
      }

      if (ended) {
        // Completed normally
        return completedResolve();
      }

      // Decide terminal vs retry
      if (terminalError) {
        if (isTerminalError(terminalError)) {
          setState("error");
          onError?.(terminalError);
          return completedReject(terminalError);
        }
      }

      // Retry budget checks
      const maxAttempts = backoff.maxAttempts ?? Infinity;
      if (attempts >= maxAttempts) {
        const giveupErr =
          terminalError ?? toAppError("SSE max attempts reached", 500);
        setState("error");
        logWarn(`[sseClient] Reached maxAttempts=${maxAttempts}. Giving up.`);
        onError?.(giveupErr);
        return completedReject(giveupErr);
      }

      const delay = computeDelayMs(attempts, backoff);
      logWarn(
        `[sseClient] Attempt ${attempts} failed${terminalError ? ` (status ${terminalError.status})` : ""}. Retrying in ~${Math.round(delay)}ms`,
      );

      await new Promise<void>((res) => setTimeout(res, delay));
      // loop continues
    }
  };

  // Start background execution (do not await here)
  void run().catch((e) => {
    const appErr = toAppError(e, 500);
    setState("error");
    onError?.(appErr);
    completedReject(appErr);
  });

  return {
    close,
    getState: () => state,
    attempts,
    completed,
  };
}
